import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { Box, Link, Text, VStack } from '@enechain/ecloud-designsystem'
import { FaInboxRegular } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'
import { useCsvImportLink } from '~/hooks/useCsvImportReturn'
import {
  CSV_UPLOADABLE_CATEGORIES,
  SOURCE_LINK_BY_CATEGORY,
} from '~/pages/Supply48Data/models/categorySourceLink'
import { routePaths } from '~/routes/routePaths'

type Props = {
  /** 表示中のカテゴリ。案内する経路がこれで変わる。 */
  category: SupplyCategory
}

/**
 * 48 点データが 1 件も出ない時に、**どうすれば表示されるか**を出す案内
 * (デザイナー指示 2026-08-31「画面にデータを表示する方法〈追加方〉を画面上に
 * 表示したい」)。
 *
 * ## 文とリンクを 1 続きにする
 *
 * 初版は「説明文: リンク」 と区切って並べており、 **文が途中で切れて読みにくい**
 * (デザイナー指摘 2026-09-01「文章が繋がってなくてわかりづらい。 説明文と導線が
 * 自然に繋がるようにしたい」)。 リンクを**文の主語側に置いて 1 文にする**
 * (「〈取引契約管理〉で契約を登録すると、 …48 コマ値が自動で入ります。」)。
 *
 * そのためリンク表記は画面名 (`nameKey`) を使う。 サイドバーの導線ボタンが使う
 * 「〜を開く」 (`labelKey`) は、 文中に置くと「取引契約管理を開くで契約を登録すると」
 * と壊れる。
 *
 * i18n は「リンクの後ろ」 だけを 1 キーに持たせる。 `Trans` は app 内で未使用のため
 * 導入せず、 **リンクが先頭に来る語順**に文を作ってキー分割を 1 箇所で済ませている。
 *
 * ## 経路はカテゴリで変わる
 *
 * 全カテゴリに同じ案内を出すと**嘘になる** (review 指摘 #6883)。
 *
 * | カテゴリ | 元データ | CSV 投入 |
 * |---|---|---|
 * | 相対取引 / 常時BU | 相対取引契約 (取引契約管理) | できる |
 * | 先物 | 相対取引契約 (受渡区分=先物で登録 → 保有量が自動派生) | **できない** |
 * | スポット・時間前 | 市場約定 (スポット価格) | **できない** |
 *
 * - 元データ画面の対応は design `supply/48data.html` の `CAT_SOURCE` が SoT
 *   ([[SOURCE_LINK_BY_CATEGORY]]、 サイドバーの「元データを開く」 と同じ表)
 * - 先物の CSV 拒否は `admin/data/uploads/supply-48-data.md` AC-SUPPLY-018 (#5736)。
 *   市場は約定由来のみでコマ単位の投入経路が無い ([[CSV_UPLOADABLE_CATEGORIES]])
 *
 * ## 空状態パネルに揃える
 *
 * `design/ui-feasibility/components/empty-panel.md` が SoT
 * (デザイナー指示 2026-09-01「空状態パネルの UI を参考に、 アイコンも表示する UI に
 * 統一したい」)。 同 md は「空の理由が**複数**あり、 理由ごとに次の行動が変わる」
 * → 1 行 alert ではなくパネル、 の分岐も定めており、 この画面はそれに当たる
 * (カテゴリごとに次の行動が変わる)。
 *
 * 値は DS `PageError` が正 (同 md 2026-07-29 の基準変更)。 React 側の canonical 実装は
 * `~/components/common/PlaceholderPage` で、 layout・token をそこに揃える:
 * icon 80px (`iconSize.emptyPanel`) / title `emptyPanelTitle` (20px/600) /
 * desc `body` + **`Text.highEmphasis`** (mid ではない) / maxWidth 480px /
 * `gap="2"` + `paddingX="4"` + `paddingY="10"`。
 *
 * glyph は **受信箱 (inbox)**。 同 md の対応表で「そもそも無い」 = 受信箱、
 * 「絞り込みの結果が 0 件」 = 虫眼鏡、 と描き分けると決まっている。 この案内は
 * 「まだデータが入っていないので入れ方を示す」 面なので前者。 色は状態によらず
 * `Object.disabled` 固定 (色に意味を持たせない、 WCAG 1.4.1)。
 */
export const NoDataGuide = ({ category }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const csvImportLink = useCsvImportLink(
    routePaths.adminUploadsSupply48Data.path,
  )

  const sourceLink = SOURCE_LINK_BY_CATEGORY[category]
  const canUploadCsv = CSV_UPLOADABLE_CATEGORIES.has(category)
  const isMarket = category === SupplyCategory.MARKET

  return (
    <VStack alignItems="center" gap="2" paddingX="4" paddingY="10">
      {/* 色は状態によらず Object.disabled 固定 (DS PageError と同じ)。
          状態は glyph と文言が担う。 受信箱 = そもそもデータが無い。 */}
      <Box color="Object.disabled">
        <FaInboxRegular boxSize={iconSize.emptyPanel} aria-hidden="true" />
      </Box>

      <Text textStyle="emptyPanelTitle" color="Text.highEmphasis">
        {t('page:supply48Data.noData.title')}
      </Text>

      {/* 本文は空状態パネルの規定どおり中央揃え・maxWidth 480px。 文ごとに <Text> を
          分けるのは、 各文にリンクが埋まっていて 1 つの pre-line 文字列にできないため
          (リンクは文の主語側に置く。 下の各ブロックのコメント参照)。 */}
      <VStack alignItems="center" gap={2} maxWidth="480px" textAlign="center">
        <Text textStyle="body" color="Text.highEmphasis" lineHeight={1.5}>
          {t('page:supply48Data.noData.desc')}
        </Text>

        {sourceLink !== undefined && (
          // `pre-line` は文言側の改行 (`\n`) を効かせるため。 日本語の 1 文が長く、
          // 折返し任せだと切れる位置が幅で変わって読みにくい
          // (デザイナー指定 2026-09-01「取引契約管理で契約を登録すると、/
          // 対象受渡日の 48 コマ値が自動で入ります。」)。 英語は改行を持たないので
          // 従来どおり折返し任せになる。
          <Text
            textStyle="body"
            color="Text.highEmphasis"
            lineHeight={1.5}
            whiteSpace="pre-line"
          >
            <Link asChild>
              <RouterLink to={sourceLink.path}>
                {t(sourceLink.nameKey)}
              </RouterLink>
            </Link>
            {t(
              isMarket
                ? 'page:supply48Data.noData.waySourceAfterMarket'
                : 'page:supply48Data.noData.waySourceAfter',
            )}
          </Text>
        )}

        {canUploadCsv && (
          <Text textStyle="body" color="Text.highEmphasis" lineHeight={1.5}>
            {/* 遷移先はツールバーの「CSVアップロード」 と同じ個別アップロード画面。
                導線を 2 種類作らない。 */}
            <Link asChild>
              <RouterLink to={csvImportLink}>
                {t('page:supply48Data.toolbar.csvUpload')}
              </RouterLink>
            </Link>
            {t('page:supply48Data.noData.wayCsvAfter')}
          </Text>
        )}

        {/* 上のリンク文と同じ理由で改行位置を文言側に持たせる (2 文を 1 行ずつに割る)。 */}
        <Text
          textStyle="body"
          color="Text.highEmphasis"
          lineHeight={1.5}
          whiteSpace="pre-line"
        >
          {t('page:supply48Data.noData.wayFilter')}
        </Text>
      </VStack>
    </VStack>
  )
}
